import { useCallback, useEffect, useState } from "react";
import { Alert, Button, Form, ProgressBar } from "react-bootstrap";
import toast from "react-hot-toast";

import {
  cancelSurveyExport,
  createSurveyExport,
  downloadSurveyExport,
  getSurveyExport,
  listSurveyExports,
  messageFromError,
} from "../../utils/api";
import { ErrorAlert, PlanLock, StatusBadge, UpgradeGrid, formatDate } from "./common";

const DATA_FORMATS = [
  ["json", "JSON"],
  ["xml", "XML"],
  ["csv", "Normalized CSV ZIP"],
];
const RENDERED_FORMATS = [
  ["pdf", "PDF"],
  ["docx", "DOCX"],
  ["xsl", "XSL stylesheet"],
];

export default function ExportControls({
  surveyId,
  responseId = null,
  filters = {},
  usage,
}) {
  const dataEnabled = Boolean(usage?.features?.dataExports);
  const renderedEnabled = Boolean(usage?.features?.renderedReports);
  const formats = [
    ...(dataEnabled ? DATA_FORMATS : []),
    ...(renderedEnabled ? RENDERED_FORMATS : []),
  ];
  const enabled = formats.length > 0;
  const [format, setFormat] = useState(formats[0]?.[0] || "json");
  const [includeIdentity, setIncludeIdentity] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [active, setActive] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const loadJobs = useCallback(async () => {
    if (!enabled) return;
    try {
      const result = await listSurveyExports(surveyId);
      setJobs(result.items || []);
    } catch (requestError) {
      setError(messageFromError(requestError, "Export history could not be loaded."));
    }
  }, [enabled, surveyId]);

  useEffect(() => { loadJobs(); }, [loadJobs]);

  useEffect(() => {
    if (!active || !["queued", "processing"].includes(active.status)) return undefined;
    const timer = window.setTimeout(async () => {
      try {
        const result = await getSurveyExport(surveyId, active.id);
        setActive(result.item);
        if (["completed", "failed", "expired"].includes(result.item.status)) {
          await loadJobs();
          if (result.item.status === "completed") toast.success("Export is ready.");
        }
      } catch (requestError) {
        setError(messageFromError(requestError, "Export status could not be refreshed."));
      }
    }, 1500);
    return () => window.clearTimeout(timer);
  }, [active, loadJobs, surveyId]);

  const create = async event => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const result = await createSurveyExport(surveyId, {
        format,
        responseId,
        filters,
        includeIdentity,
      });
      setActive(result.item);
      setJobs(current => [result.item, ...current.filter(item => item.id !== result.item.id)]);
    } catch (requestError) {
      setError(messageFromError(requestError, "The export could not be started."));
    } finally {
      setBusy(false);
    }
  };

  const download = async job => {
    setBusy(true);
    setError("");
    try {
      const fresh = job.download
        ? job
        : (await getSurveyExport(surveyId, job.id)).item;
      if (!fresh.download) throw new Error("The export download is not ready.");
      const blob = await downloadSurveyExport(fresh.download);
      const href = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = href;
      anchor.download = fresh.fileName || `survey-export.${fresh.format}`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(href);
      toast.success("Export downloaded.");
      await loadJobs();
    } catch (requestError) {
      setError(messageFromError(requestError, "The export could not be downloaded."));
    } finally {
      setBusy(false);
    }
  };

  const cancel = async job => {
    setBusy(true);
    try {
      const result = await cancelSurveyExport(surveyId, job.id);
      setActive(result.item);
      toast.success("Export cancelled.");
      await loadJobs();
    } catch (requestError) {
      setError(messageFromError(requestError, "The export could not be cancelled."));
    } finally {
      setBusy(false);
    }
  };

  if (!enabled) {
    return (
      <div className="fera-print-hidden">
        <section className="fera-panel mt-4">
          <div className="fera-panel-heading">
            <div><p className="fera-eyebrow">Exports</p><h2>Download report data and documents</h2></div>
            <PlanLock>Paid feature</PlanLock>
          </div>
          <Alert variant="info" className="mb-0">
            HTML reporting and printing remain available. JSON, XML, normalized CSV, PDF, DOCX, and XSL downloads require a paid plan.
          </Alert>
        </section>
        <UpgradeGrid access={usage?.access} title="Add report and data exports" />
      </div>
    );
  }

  return (
    <section className="fera-panel mt-4 fera-print-hidden" aria-labelledby="export-heading">
      <div className="fera-panel-heading">
        <div><p className="fera-eyebrow">Exports</p><h2 id="export-heading">Download report data and documents</h2></div>
      </div>
      <ErrorAlert error={error} onRetry={loadJobs} />
      <Form className="fera-export-form" onSubmit={create}>
        <Form.Group controlId={`export-format-${responseId || "aggregate"}`}>
          <Form.Label>Format</Form.Label>
          <Form.Select value={format} onChange={event => setFormat(event.target.value)}>
            {formats.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </Form.Select>
        </Form.Group>
        <Form.Check
          id={`export-identity-${responseId || "aggregate"}`}
          type="checkbox"
          label="Include permitted respondent identity"
          checked={includeIdentity}
          onChange={event => setIncludeIdentity(event.target.checked)}
        />
        <Button type="submit" disabled={busy}>Create export</Button>
      </Form>
      {active && ["queued", "processing"].includes(active.status) ? (
        <div className="fera-export-progress" role="status" aria-live="polite">
          <div><strong>Preparing {active.format.toUpperCase()}</strong><span>{active.progress}%</span></div>
          <ProgressBar now={active.progress} />
          <Button size="sm" variant="outline-secondary" disabled={busy} onClick={() => cancel(active)}>Cancel</Button>
        </div>
      ) : null}
      {jobs.length ? (
        <div className="fera-export-jobs">
          {jobs.slice(0, 5).map(job => (
            <div key={job.id}>
              <div>
                <strong>{job.fileName || `${job.format.toUpperCase()} export`}</strong>
                <span>{formatDate(job.createdAt)} · <StatusBadge status={job.status} /></span>
              </div>
              {job.status === "completed" ? (
                <Button size="sm" variant="outline-primary" disabled={busy} onClick={() => download(job)}>Download</Button>
              ) : null}
              {job.status === "failed" ? <small className="text-danger">{job.lastError}</small> : null}
            </div>
          ))}
        </div>
      ) : null}
    </section>
  );
}
